import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from "ngx-intl-tel-input";

const DEFAULT_OUT_FOR_DELIVERY_MESSAGE = "Our Colleague/delivery partner is on the way to deliver your order , he will be at your place between 45 -75 minutes";

@Component({
  selector: "app-vending-order-modal",
  templateUrl: "./vending-order-modal.component.html",
  styleUrls: ["./vending-order-modal.component.css"],
})
export class VendingOrderModalComponent implements OnInit, OnChanges {
  @Input() title: string = "Order Details";
  @Input() modalType: "view" | "delete" | "status" = "view";
  @Input() order: any;
  @Input() actionType: string = "";
  @Input() requiresReason: boolean = false;
  reason: string = "";
  riderPhone: any = null;
  message: string = "";
  riderCountryISO: CountryISO = CountryISO.India;
  /** Current dial code digits (e.g. "46") for payload when input shows national number only. */
  riderDialCode: string = "91";
  /** True after setOutForDeliveryDefaults() has run so phone input is created with correct dial code/number. */
  outForDeliveryDefaultsApplied = false;

  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.India,
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private cdr: ChangeDetectorRef
  ) {}
  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes["order"] || changes["actionType"]) &&
      this.actionType?.toLowerCase() === "out for delivery" &&
      this.order
    ) {
      this.setOutForDeliveryDefaults();
    }
  }

  /** Call after modal open when parent sets order/actionType (e.g. for NgbModal). Sets rider dial code and number from order?.operator_id?.dialling_code / mobile. */
  setOutForDeliveryDefaults(): void {
    if (this.actionType?.toLowerCase() !== "out for delivery") return;

    const mobile = (this.order?.operator_id?.mobile ?? "").toString().trim();
    // Use operator's dialling_code dynamically (any dial code from API); fallback to 91 only when missing
    const dialCodeRaw =
      (this.order?.operator_id?.dialling_code ?? "")
        .toString()
        .trim()
        .replace(/^\+/, "") || "91";

    const digitsOnly = mobile.replace(/\D/g, "").replace(/^0+/, "");

    // Store dial code for payload; input shows national number only (no +46 in the field)
    this.riderDialCode = dialCodeRaw;
    this.riderPhone = digitsOnly || null;

    // Set country dropdown to match the operator's dial code
    this.riderCountryISO = this.getCountryISOFromDialCode(dialCodeRaw);

    this.message = this.message || DEFAULT_OUT_FOR_DELIVERY_MESSAGE;
    this.outForDeliveryDefaultsApplied = true;
    this.cdr.detectChanges();
  }

  /** Maps dial code (digits only, e.g. "971") to CountryISO for the phone input. Unknown codes default to India. */
  private getCountryISOFromDialCode(dialCode: string): CountryISO {
    const code = dialCode.replace(/\D/g, "");
    const map: Record<string, CountryISO> = {
      "1": CountryISO.UnitedStates,
      "7": CountryISO.Russia,
      "20": CountryISO.Egypt,
      "27": CountryISO.SouthAfrica,
      "30": CountryISO.Greece,
      "31": CountryISO.Netherlands,
      "32": CountryISO.Belgium,
      "33": CountryISO.France,
      "34": CountryISO.Spain,
      "36": CountryISO.Hungary,
      "39": CountryISO.Italy,
      "40": CountryISO.Romania,
      "41": CountryISO.Switzerland,
      "43": CountryISO.Austria,
      "44": CountryISO.UnitedKingdom,
      "45": CountryISO.Denmark,
      "46": CountryISO.Sweden,
      "47": CountryISO.Norway,
      "48": CountryISO.Poland,
      "49": CountryISO.Germany,
      "51": CountryISO.Peru,
      "52": CountryISO.Mexico,
      "53": CountryISO.Cuba,
      "54": CountryISO.Argentina,
      "55": CountryISO.Brazil,
      "56": CountryISO.Chile,
      "57": CountryISO.Colombia,
      "58": CountryISO.Venezuela,
      "60": CountryISO.Malaysia,
      "61": CountryISO.Australia,
      "62": CountryISO.Indonesia,
      "63": CountryISO.Philippines,
      "64": CountryISO.NewZealand,
      "65": CountryISO.Singapore,
      "66": CountryISO.Thailand,
      "81": CountryISO.Japan,
      "82": CountryISO.SouthKorea,
      "84": CountryISO.Vietnam,
      "86": CountryISO.China,
      "90": CountryISO.Turkey,
      "91": CountryISO.India,
      "92": CountryISO.Pakistan,
      "93": CountryISO.Afghanistan,
      "94": CountryISO.SriLanka,
      "95": CountryISO.Myanmar,
      "98": CountryISO.Iran,
      "212": CountryISO.Morocco,
      "213": CountryISO.Algeria,
      "216": CountryISO.Tunisia,
      "218": CountryISO.Libya,
      "220": CountryISO.Gambia,
      "221": CountryISO.Senegal,
      "222": CountryISO.Mauritania,
      "223": CountryISO.Mali,
      "224": CountryISO.Guinea,
      "225": CountryISO.CôteDIvoire,
      "226": CountryISO.BurkinaFaso,
      "227": CountryISO.Niger,
      "228": CountryISO.Togo,
      "229": CountryISO.Benin,
      "230": CountryISO.Mauritius,
      "231": CountryISO.Liberia,
      "232": CountryISO.SierraLeone,
      "233": CountryISO.Ghana,
      "234": CountryISO.Nigeria,
      "235": CountryISO.Chad,
      "236": CountryISO.CentralAfricanRepublic,
      "237": CountryISO.Cameroon,
      "238": CountryISO.CapeVerde,
      "240": CountryISO.EquatorialGuinea,
      "241": CountryISO.Gabon,
      "242": CountryISO.CongoRepublicCongoBrazzaville,
      "243": CountryISO.CongoDRCJamhuriYaKidemokrasiaYaKongo,
      "244": CountryISO.Angola,
      "245": CountryISO.GuineaBissau,
      "248": CountryISO.Seychelles,
      "249": CountryISO.Sudan,
      "250": CountryISO.Rwanda,
      "251": CountryISO.Ethiopia,
      "252": CountryISO.Somalia,
      "253": CountryISO.Djibouti,
      "254": CountryISO.Kenya,
      "255": CountryISO.Tanzania,
      "256": CountryISO.Uganda,
      "257": CountryISO.Burundi,
      "258": CountryISO.Mozambique,
      "260": CountryISO.Zambia,
      "261": CountryISO.Madagascar,
      "262": CountryISO.Réunion,
      "263": CountryISO.Zimbabwe,
      "264": CountryISO.Namibia,
      "265": CountryISO.Malawi,
      "266": CountryISO.Lesotho,
      "267": CountryISO.Botswana,
      "268": CountryISO.Swaziland,
      "269": CountryISO.Comoros,
      "290": CountryISO.SaintHelena,
      "291": CountryISO.Eritrea,
      "350": CountryISO.Gibraltar,
      "351": CountryISO.Portugal,
      "352": CountryISO.Luxembourg,
      "353": CountryISO.Ireland,
      "354": CountryISO.Iceland,
      "355": CountryISO.Albania,
      "356": CountryISO.Malta,
      "357": CountryISO.Cyprus,
      "358": CountryISO.Finland,
      "359": CountryISO.Bulgaria,
      "370": CountryISO.Lithuania,
      "371": CountryISO.Latvia,
      "372": CountryISO.Estonia,
      "373": CountryISO.Moldova,
      "374": CountryISO.Armenia,
      "375": CountryISO.Belarus,
      "376": CountryISO.Andorra,
      "377": CountryISO.Monaco,
      "378": CountryISO.SanMarino,
      "379": CountryISO.VaticanCity,
      "380": CountryISO.Ukraine,
      "381": CountryISO.Serbia,
      "382": CountryISO.Montenegro,
      "383": CountryISO.Kosovo,
      "385": CountryISO.Croatia,
      "386": CountryISO.Slovenia,
      "387": CountryISO.BosniaAndHerzegovina,
      "389": CountryISO.Macedonia,
      "420": CountryISO.CzechRepublic,
      "421": CountryISO.Slovakia,
      "423": CountryISO.Liechtenstein,
      "500": CountryISO.FalklandIslands,
      "501": CountryISO.Belize,
      "502": CountryISO.Guatemala,
      "503": CountryISO.ElSalvador,
      "504": CountryISO.Honduras,
      "505": CountryISO.Nicaragua,
      "506": CountryISO.CostaRica,
      "507": CountryISO.Panama,
      "509": CountryISO.Haiti,
      "590": CountryISO.Guadeloupe,
      "591": CountryISO.Bolivia,
      "592": CountryISO.Guyana,
      "593": CountryISO.Ecuador,
      "594": CountryISO.FrenchGuiana,
      "595": CountryISO.Paraguay,
      "596": CountryISO.Martinique,
      "597": CountryISO.Suriname,
      "598": CountryISO.Uruguay,
      "599": CountryISO.Curaçao,
      "670": CountryISO.TimorLeste,
      "673": CountryISO.Brunei,
      "674": CountryISO.Nauru,
      "675": CountryISO.PapuaNewGuinea,
      "676": CountryISO.Tonga,
      "677": CountryISO.SolomonIslands,
      "678": CountryISO.Vanuatu,
      "679": CountryISO.Fiji,
      "680": CountryISO.Palau,
      "682": CountryISO.CookIslands,
      "683": CountryISO.Niue,
      "685": CountryISO.Samoa,
      "686": CountryISO.Kiribati,
      "687": CountryISO.NewCaledonia,
      "688": CountryISO.Tuvalu,
      "689": CountryISO.FrenchPolynesia,
      "690": CountryISO.Tokelau,
      "691": CountryISO.Micronesia,
      "692": CountryISO.MarshallIslands,
      "852": CountryISO.HongKong,
      "853": CountryISO.Macau,
      "855": CountryISO.Cambodia,
      "856": CountryISO.Laos,
      "880": CountryISO.Bangladesh,
      "886": CountryISO.Taiwan,
      "960": CountryISO.Maldives,
      "961": CountryISO.Lebanon,
      "962": CountryISO.Jordan,
      "963": CountryISO.Syria,
      "964": CountryISO.Iraq,
      "965": CountryISO.Kuwait,
      "966": CountryISO.SaudiArabia,
      "967": CountryISO.Yemen,
      "968": CountryISO.Oman,
      "970": CountryISO.Palestine,
      "971": CountryISO.UnitedArabEmirates,
      "972": CountryISO.Israel,
      "973": CountryISO.Bahrain,
      "974": CountryISO.Qatar,
      "975": CountryISO.Bhutan,
      "976": CountryISO.Mongolia,
      "977": CountryISO.Nepal,
      "992": CountryISO.Tajikistan,
      "993": CountryISO.Turkmenistan,
      "994": CountryISO.Azerbaijan,
      "995": CountryISO.Georgia,
      "996": CountryISO.Kyrgyzstan,
      "998": CountryISO.Uzbekistan,
    };
    return map[code] ?? CountryISO.India;
  }

  isOutForDelivery(): boolean {
    return this.actionType?.toLowerCase() === "out for delivery";
  }

  /** Called when user changes country in the phone input; keeps riderDialCode in sync for payload. */
  onRiderCountryChange(country: { dialCode?: string }): void {
    const raw = (country?.dialCode ?? "").toString().trim().replace(/^\+/, "");
    if (raw) this.riderDialCode = raw;
  }

  /** Returns full rider mobile digits (dial code + national) for the API payload. */
  getRiderMobileForPayload(): string {
    if (!this.riderPhone) return "";
    if (typeof this.riderPhone === "string") {
      const national = this.riderPhone.trim().replace(/\D/g, "") || "";
      return (this.riderDialCode || "") + national;
    }
    const e164 = (this.riderPhone.e164Number ?? "").toString().replace(/\D/g, "");
    if (e164) return e164;
    const national = (this.riderPhone.nationalNumber ?? "").toString().replace(/\s/g, "").replace(/\D/g, "").replace(/^0+/, "") || "";
    const dial = (this.riderPhone.dialCode ?? "").toString().replace(/\D/g, "") || "";
    return dial + national;
  }

  hasValidRiderMobile(): boolean {
    return !!this.getRiderMobileForPayload();
  }

  getConfirmPayload(): unknown {
    if (this.isOutForDelivery()) {
      return { riderMobile: this.getRiderMobileForPayload(), message: this.message };
    }
    return this.requiresReason ? this.reason : true;
  }

  getStatusFromAction(action: string): string {
    const statusMap: { [key: string]: string } = {
      accept: "accepted",
      "Out for delivery": "outForDelivery",
      complete: "completed",
      cancel: "cancelled",
      reject: "cancelled",
    };
    return statusMap[action.toLowerCase()] || "";
  }

  getActionDisplayText(action: string): string {
    const displayMap: { [key: string]: string } = {
      accept: "accept",
      "out for delivery": "mark as out for delivery",
      complete: "complete",
      "ready for pickup": "mark as ready for pickup",
      cancel: "cancel",
      reject: "reject",
    };
    return displayMap[action.toLowerCase()] || action;
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case "pending":
        return "badge-warning";
      case "accepted":
        return "badge-info";
      case "outfordelivery":
        return "badge-primary";
      case "completed":
        return "badge-success";
      case "cancelled":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  }
}
